import { useMemo, type ReactNode } from 'react';
import { transformDestinationEvent } from '@eventbrite/consumer-search.event-renderer';
import type { DestinationEvent, FormattedEvent } from '@eventbrite/consumer-search.event-renderer';
import { useIsMobileDevice } from '@eventbrite/legacy-infra.feature-detection';
import { defineMessages, useIntl } from 'react-intl';
import { useSavedEventIds } from '@/hooks/useSavedEventIds';
import type { EventsDisplayMode } from '../EventsViewToggle/EventsViewToggle';
import { OrganizerHorizontalEventCard } from '../OrganizerHorizontalEventCard/OrganizerHorizontalEventCard';
import { OrganizerVerticalEventCard } from '../OrganizerVerticalEventCard/OrganizerVerticalEventCard';
import LoadingBucket from './LoadingBucket/LoadingBucket';
import styles from './EventsBucket.module.scss';

// TODO: review this config
const SHARE_UTM_OPTIONS = {
  'utm-campaign': 'social',
  'utm-content': 'attendeeshare',
  'utm-medium': 'discovery',
  'utm-term': 'listing',
  'utm-source': 'cp',
} as const;

const messages = defineMessages({
  today: {
    id: 'Today',
    defaultMessage: 'Today',
  },
  yesterday: {
    id: 'Yesterday',
    defaultMessage: 'Yesterday',
  },
  tomorrow: {
    id: 'Tomorrow',
    defaultMessage: 'Tomorrow',
  },
});

interface EventGroup {
  key: string;
  primaryLabel: string;
  secondaryLabel: string;
  events: FormattedEvent[];
}

function getSafeLocale(locale: string) {
  const normalizedLocale = locale.replace(/_/g, '-');

  try {
    const [supportedLocale] = Intl.DateTimeFormat.supportedLocalesOf([normalizedLocale]);
    return supportedLocale ?? 'en-US';
  } catch {
    return 'en-US';
  }
}

function createDateFormatter(locale: string, options: Intl.DateTimeFormatOptions) {
  return new Intl.DateTimeFormat(getSafeLocale(locale), options);
}

function toDateKey(date: Date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function addDays(date: Date, amount: number) {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + amount);
  return nextDate;
}

function parseEventDate(dateKey?: string) {
  if (!dateKey) {
    return null;
  }

  const [year, month, day] = dateKey.split('-').map(Number);
  if (!year || !month || !day) {
    return null;
  }

  return new Date(year, month - 1, day, 12);
}

function buildEventGroups(
  events: FormattedEvent[],
  locale: string,
  labels: { today: string; yesterday: string; tomorrow: string },
) {
  const todayKey = toDateKey(new Date());
  const yesterdayKey = toDateKey(addDays(new Date(), -1));
  const tomorrowKey = toDateKey(addDays(new Date(), 1));
  const monthDayFormatter = createDateFormatter(locale, {
    month: 'long',
    day: 'numeric',
  });
  const weekdayFormatter = createDateFormatter(locale, {
    weekday: 'long',
  });

  return events.reduce<EventGroup[]>((groups, event) => {
    const dateKey = event.startDate || `unknown-${event.id}`;
    const eventDate = parseEventDate(event.startDate);
    const weekdayLabel = eventDate ? weekdayFormatter.format(eventDate) : '';
    let primaryLabel = eventDate ? monthDayFormatter.format(eventDate) : '';

    if (dateKey === todayKey) {
      primaryLabel = labels.today;
    } else if (dateKey === yesterdayKey) {
      primaryLabel = labels.yesterday;
    } else if (dateKey === tomorrowKey) {
      primaryLabel = labels.tomorrow;
    }

    const previousGroup = groups[groups.length - 1];
    if (previousGroup?.key === dateKey) {
      previousGroup.events.push(event);
      return groups;
    }

    groups.push({
      key: dateKey,
      primaryLabel,
      secondaryLabel: weekdayLabel,
      events: [event],
    });

    return groups;
  }, []);
}

/**
 * Client-side auth for Save/Share. `unknown` until `useOrganizerUserData` resolves;
 * hide actions in that window so signed-in users don't hit the anonymous flow.
 * Not resolved on SSR: the page is edge-cached (`public, s-maxage=600`).
 */
export type AuthState = 'unknown' | 'anonymous' | 'authenticated';

interface Props {
  events?: DestinationEvent[];
  loadingState?: { isFeLoading: boolean };
  bucketName: string;
  locale: string;
  organizerId: string;
  authState?: AuthState;
  variant?: EventsDisplayMode;
  groupByDay?: boolean;
  mobileHeaderAccessory?: ReactNode;
  showSaveAction?: boolean;
  showShareAction?: boolean;
  highlightedEventId?: string;
  renderMoreActions?: (event: DestinationEvent) => ReactNode;
}

export default function EventsBucket({
  events = [],
  loadingState = { isFeLoading: true },
  bucketName,
  locale,
  organizerId,
  authState = 'unknown',
  variant = 'grid',
  groupByDay = false,
  mobileHeaderAccessory,
  showSaveAction = false,
  showShareAction = false,
  highlightedEventId,
  renderMoreActions,
}: Props) {
  const intl = useIntl();
  const isMobile = useIsMobileDevice(660);
  const eventIds = useMemo(() => events.map((e) => e.id), [events]);
  const saveLookupEnabled = showSaveAction && authState === 'authenticated';
  const { savedEventIds, isSaveStateReady } = useSavedEventIds(
    organizerId,
    eventIds,
    saveLookupEnabled,
  );
  const canShowSaveAction =
    showSaveAction && authState !== 'unknown' && (authState === 'anonymous' || isSaveStateReady);
  const personalizedEvents = useMemo(() => {
    if (savedEventIds.size === 0) return events;
    return events.map((e) =>
      savedEventIds.has(e.id) ? { ...e, saves: { saved_by_you: true } } : e,
    );
  }, [events, savedEventIds]);
  const formattedEvents = useMemo(
    () => personalizedEvents.map(transformDestinationEvent),
    [personalizedEvents],
  );
  const eventsById = useMemo(() => new Map(events.map((event) => [event.id, event])), [events]);
  const groupedEvents = useMemo(
    () =>
      buildEventGroups(formattedEvents, locale, {
        today: intl.formatMessage(messages.today),
        yesterday: intl.formatMessage(messages.yesterday),
        tomorrow: intl.formatMessage(messages.tomorrow),
      }),
    [formattedEvents, intl, locale],
  );
  const shouldRenderGroupedList = variant === 'list' || groupByDay;

  const isLoading = loadingState.isFeLoading;
  if (isLoading) {
    return (
      <div className={styles.bucketContainer}>
        <LoadingBucket count={isMobile ? 2 : 4} />
      </div>
    );
  }

  if (formattedEvents.length === 0) {
    return null;
  }

  const renderEventCard = (event: FormattedEvent) => {
    const sourceEvent = eventsById.get(event.id);
    const moreActions = sourceEvent ? renderMoreActions?.(sourceEvent) : undefined;

    return variant === 'grid' ? (
      <div className={styles.actionSurface}>
        <OrganizerVerticalEventCard
          locale={locale}
          event={event}
          statsigLocationString={`Organizer Profile - ${bucketName}`}
          isAuthenticated={authState === 'authenticated'}
          isLazyImage={false}
          affCode="ebdsoporgprofile"
          bucketLabel={bucketName}
          showSaveAction={canShowSaveAction}
          showShareAction={showShareAction && authState !== 'unknown'}
          isHighlighted={event.id === highlightedEventId}
          hasPinAction={!!moreActions}
          shareOptions={showShareAction ? { utmOptions: SHARE_UTM_OPTIONS } : undefined}
        />
        {moreActions ? <div className={styles.pinAction}>{moreActions}</div> : null}
      </div>
    ) : (
      <OrganizerHorizontalEventCard
        locale={locale}
        event={event}
        statsigLocationString={`Organizer Profile - ${bucketName}`}
        authState={authState}
        isLazyImage={false}
        affCode="ebdsoporgprofile"
        bucketName={bucketName}
        showSaveAction={canShowSaveAction}
        showShareAction={showShareAction && authState !== 'unknown'}
        shareOptions={showShareAction ? { utmOptions: SHARE_UTM_OPTIONS } : undefined}
        moreActions={moreActions}
      />
    );
  };

  const renderGroupedContent = () => (
    <div className={styles.groupedContent} data-mobile={isMobile}>
      {groupedEvents.map((group, index) => (
        <section key={group.key} className={styles.dayGroup}>
          {!isMobile ? (
            <div className={styles.dayIndicator} aria-hidden>
              <span className={styles.dayMarker} />
              <span className={styles.dayLine} />
            </div>
          ) : null}
          <div className={styles.daySection}>
            <div className={styles.dayHeader}>
              <div className={styles.dayHeaderLabels}>
                <span className={styles.dayPrimaryLabel}>{group.primaryLabel}</span>
                {group.secondaryLabel ? (
                  <span className={styles.daySecondaryLabel}>{group.secondaryLabel}</span>
                ) : null}
              </div>
              {index === 0 && mobileHeaderAccessory ? (
                <div className={styles.dayHeaderAccessory}>{mobileHeaderAccessory}</div>
              ) : null}
            </div>
            <div className={styles.dayEvents}>
              {group.events.map((event) => (
                <div key={event.id} className={styles.groupedCardCell}>
                  {renderEventCard(event)}
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}
    </div>
  );

  if (variant === 'grid') {
    return (
      <div className={styles.bucketContainer}>
        <div className={styles.gridDesktopContent}>
          <div className={styles.bucketContent} data-variant={variant}>
            {formattedEvents.map((event: FormattedEvent) => (
              <div key={event.id} className={styles.cardCell}>
                {renderEventCard(event)}
              </div>
            ))}
          </div>
        </div>
        <div className={styles.gridMobileGrouped}>{renderGroupedContent()}</div>
      </div>
    );
  }

  if (shouldRenderGroupedList) {
    return <div className={styles.bucketContainer}>{renderGroupedContent()}</div>;
  }

  return (
    <div className={styles.bucketContainer}>
      {mobileHeaderAccessory ? (
        <div className={styles.mobileCalendarAccessoryRow}>{mobileHeaderAccessory}</div>
      ) : null}
      <div className={styles.bucketContent} data-variant={variant}>
        {formattedEvents.map((event: FormattedEvent) => (
          <div key={event.id} className={styles.cardCell}>
            {renderEventCard(event)}
          </div>
        ))}
      </div>
    </div>
  );
}
